import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ManifestBlock, ManifestDictionary, ManifestField } from "@/api/integration";
import { Card, SectionHead } from "@/components/brand/Card";
import { Pill } from "@/components/brand/Pill";
import { Toolbar, ToolbarSearch } from "@/components/brand/Toolbar";

import { fieldDescription, fieldShape } from "./fieldText";

const anchor = (name: string) => `manifest-block-${name}`;

/**
 * O manifesto bloco a bloco, da raiz para dentro, como a api o descreve. Cada bloco diz onde aparece
 * no manifesto, e o campo que é outro bloco leva até ele.
 */
export function ManifestView({ manifest }: { manifest: ManifestDictionary }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const blocks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return manifest.blocks;
    return manifest.blocks.filter(
      (block) =>
        block.name.toLowerCase().includes(q) ||
        block.paths.some((path) => path.toLowerCase().includes(q)) ||
        fieldDescription(t, block).toLowerCase().includes(q) ||
        block.fields.some(
          (field) =>
            field.name.toLowerCase().includes(q) ||
            fieldDescription(t, field).toLowerCase().includes(q),
        ),
    );
  }, [manifest, query, t]);

  return (
    <section className="space-y-5">
      <SectionHead
        overline={t("EXTRACTION MANIFEST")}
        title={t("Manifest {{version}}", { version: manifest.manifest_version })}
        tag={`${manifest.blocks.length} ${t("blocks")}`}
        sub={t(
          "The list of what to extract, which myGROW generates for each project and the extractor reads before opening the load. Each block shows where it appears in the manifest and its fields. This version covers ECC and S/4HANA.",
        )}
        right={<span className="mono text-[11px] text-[#7b8799] break-all">{manifest.schema_id}</span>}
      />
      <Card>
        <Toolbar>
          <ToolbarSearch
            placeholder={t("Search block, field or description…")}
            width={320}
            value={query}
            onChange={setQuery}
          />
        </Toolbar>
      </Card>
      {blocks.length === 0 && (
        <p className="text-center py-12 text-[#7b8799] text-sm">
          {t("No block or field matches your search.")}
        </p>
      )}
      {blocks.map((block) => (
        <BlockCard key={block.name} block={block} />
      ))}
    </section>
  );
}

function BlockCard({ block }: { block: ManifestBlock }) {
  const { t } = useTranslation();
  return (
    <Card>
      <div id={anchor(block.name)} data-testid={anchor(block.name)} className="scroll-mt-6">
        <div className="px-4 py-3 border-b border-[#f2f4f7] flex flex-col gap-1">
          <span className="mono text-[14px] font-semibold text-[#08254c]">{block.name}</span>
          <span className="text-[13px] text-[#596b85]">{fieldDescription(t, block)}</span>
          {block.paths.length === 0 ? (
            <span className="mono text-[11px] text-[#7b8799]">{t("Manifest root")}</span>
          ) : (
            block.paths.map((path) => (
              <span key={path} className="mono text-[11px] text-[#7b8799] break-all">
                {path}
              </span>
            ))
          )}
        </div>
        {block.fields.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: "24%" }}>{t("Field")}</th>
                  <th style={{ width: "22%" }}>{t("Type")}</th>
                  <th style={{ width: "12%" }}>{t("Required")}</th>
                  <th>{t("Description")}</th>
                </tr>
              </thead>
              <tbody>
                {block.fields.map((field) => (
                  <tr key={field.name}>
                    <td className="mono text-[13px] text-[#08254c]">{field.name}</td>
                    <td>
                      <FieldType field={field} />
                    </td>
                    <td>
                      {field.required ? (
                        <Pill tone="navy">{t("required")}</Pill>
                      ) : (
                        <span className="muted text-[12px]">{t("optional")}</span>
                      )}
                    </td>
                    <td className="text-[13px] text-[#08254c]">{fieldDescription(t, field)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

function FieldType({ field }: { field: ManifestField }) {
  const link = field.ref && (
    <a href={`#${anchor(field.ref)}`} className="underline text-[#08254c]">
      {field.ref}
    </a>
  );
  return (
    <>
      <span className="mono text-[12px] text-[#596b85]">
        {link ? (field.type === "array" ? <>array&lt;{link}&gt;</> : link) : fieldShape(field)}
      </span>
      {field.nullable && <div className="mono muted text-[10px]">null</div>}
      {field.values && (
        <div className="mono muted text-[10px] break-all">{field.values.join(" | ")}</div>
      )}
    </>
  );
}
